import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatsGrid } from '@/components/ui/stats-grid';

function PanelSkeleton({ buttonCount = 2 }: { buttonCount?: number }) {
  return (
    <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
      <CardHeader className="space-y-1.5">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-3 w-56" />
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Key-value pairs (2-col) */}
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-28" />
            </div>
          ))}
        </div>
        {/* Action buttons */}
        <div className="flex gap-3">
          {Array.from({ length: buttonCount }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-36 rounded-md" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TableSkeleton({ columns, rows = 5 }: { columns: number[]; rows?: number }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/30">
            {columns.map((w, i) => (
              <th key={i} className="px-3 py-2">
                <Skeleton className="h-3" style={{ width: w }} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, row) => (
            <tr key={row} className="border-b last:border-0">
              {columns.map((w, col) => (
                <td key={col} className="px-3 py-2">
                  <Skeleton className="h-3" style={{ width: w + (row % 2) * 6 }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminSkeleton() {
  return (
    <div className="space-y-4">
      {/* Page header */}
      <Skeleton className="h-9 w-48" />

      {/* Stats grid */}
      <StatsGrid columns={4} isLoading />

      {/* Snowflake Sync + Policy Scoring (two panels) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PanelSkeleton buttonCount={2} />
        <PanelSkeleton buttonCount={1} />
      </div>

      {/* Rate Ingestion panel (full width) */}
      <Card className="bg-surface-0" style={{ boxShadow: 'var(--shadow-1)' }}>
        <CardHeader className="space-y-1.5">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-3 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-28" />
              </div>
            ))}
          </div>
          <Skeleton className="h-9 w-40 rounded-md" />
        </CardContent>
      </Card>

      {/* Database Status panel */}
      <Card style={{ boxShadow: 'var(--inset-shadow)' }}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1.5">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-3 w-52" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Recent Sync History table */}
          <div>
            <Skeleton className="mb-2 h-4 w-36" />
            <TableSkeleton columns={[100, 56, 48, 56, 48]} />
          </div>

          {/* Recent Scoring Jobs table */}
          <div>
            <Skeleton className="mb-2 h-4 w-36" />
            <TableSkeleton columns={[100, 48, 48, 56, 48]} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
